import { EntrySettings } from '@/api/types/entry-types';
import theme from '@/styles/DybrTheme';
import LockIcon from '@/styles/Icons/Lock';
import { useState } from 'react';
import { ArrowContainer, Popover, type PopoverState } from 'react-tiny-popover';
import { ARROW_HEIGHT } from '../constants';
import Loading from '../Loading';
import { useEntryAccessInfo } from './hooks/useEntryAccessInfo';

interface EntryAccessLockProps {
  permissions: EntrySettings['permissions'];
  isCommunity: boolean;
  canEdit: boolean;
}

interface AccessLevelContentProps {
  label: string;
  listNames: string[];
  listNamesLoading: boolean;
  listNamesError: boolean;
}

function AccessLevelContent({
  label,
  listNames,
  listNamesLoading,
  listNamesError
}: AccessLevelContentProps) {
  const renderListNamesInfo = () => {
    if (listNamesLoading) {
      return (
        <>
          {' '}
          <Loading inline />
        </>
      );
    }

    if (listNamesError) {
      return null; // Show plain access level
    }

    if (listNames && listNames.length > 0) {
      return <> "{listNames.join('", "')}"</>;
    }

    return null;
  };

  return (
    <div
      style={{
        backgroundColor: theme.backgroundLighter,
        padding: '8px 12px',
        fontSize: '14px',
        maxWidth: '600px'
      }}
      className="blog-entry-title-access-level"
    >
      {label}
      {renderListNamesInfo()}
    </div>
  );
}

export function EntryAccessLock({
  permissions,
  isCommunity: isCommunityEntry,
  canEdit
}: EntryAccessLockProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { label, listNames, isLoading, error, shouldShowLockIcon } =
    useEntryAccessInfo({
      permissions,
      isCommunityEntry,
      canEdit
    });

  if (!shouldShowLockIcon) {
    return null;
  }

  const renderPopoverContent = ({
    position,
    childRect,
    popoverRect
  }: PopoverState) => {
    return (
      <ArrowContainer
        position={position}
        childRect={childRect}
        popoverRect={popoverRect}
        arrowColor={theme.backgroundLighter}
        arrowSize={ARROW_HEIGHT}
        style={{ filter: 'drop-shadow( 0px 0px 6px rgba(0, 0, 0, 0.3))' }}
      >
        <AccessLevelContent
          label={label || ''}
          listNames={listNames || []}
          listNamesLoading={isLoading}
          listNamesError={Boolean(error)}
        />
      </ArrowContainer>
    );
  };

  return (
    <Popover
      padding={5}
      isOpen={isPopoverOpen}
      positions={['top']}
      content={renderPopoverContent}
      onClickOutside={() => setIsPopoverOpen(false)}
      reposition={true}
    >
      <div
        className="entry-lock-icon"
        data-testid="lock-icon-testid"
        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
      >
        <LockIcon className="" />
      </div>
    </Popover>
  );
}
